import { useQuery } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { costsApi, type CostEvent } from "@/api/costs";
import { DollarSign } from "lucide-react";

export function Costs() {
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: summary } = useQuery({
    queryKey: ["costs", "summary", tenantId],
    queryFn: () => costsApi.summary(tenantId),
  });

  const { data: events = [], isLoading } = useQuery({
    queryKey: ["costs", tenantId],
    queryFn: () => costsApi.list(tenantId),
  });

  const agentName = (id: string | null) => {
    if (!id) return "—";
    return agents.find((a) => a.id === id)?.name || id.slice(0, 8);
  };

  return (
    <CenterPanel title="Costs">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-text-secondary">Total Spend</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">
              ${summary?.totalDollars?.toFixed(2) ?? "0.00"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-text-secondary">Events</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{summary?.eventCount ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-text-secondary">Agents</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-semibold">{agents.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Event log */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost Events</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-text-muted">Loading...</p>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <DollarSign size={32} className="mb-2 text-text-muted" />
              <p className="text-sm text-text-secondary">No cost events recorded.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-1">
              {events.map((ev) => (
                <div key={ev.id} className="flex items-center justify-between rounded px-2 py-2 hover:bg-surface-hover">
                  <div>
                    <p className="text-sm text-text-primary">{ev.eventType}</p>
                    <p className="text-xs text-text-muted">
                      {agentName(ev.agentId)}
                      {ev.description && ` · ${ev.description}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-mono font-medium">
                      ${(ev.amountCents / 100).toFixed(2)}
                    </span>
                    <span className="text-xs text-text-muted">
                      {new Date(ev.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </CenterPanel>
  );
}
